import { AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssessmentDto,
  OrgPublicProfileDto,
  OrganizationEntryDto,
  useListAssessmentsQuery,
} from "../../../../autogen/bff-api";
import { useSub } from "../../../../common/subscription/useSub";
import { requireBooleanEnvVar } from "../../../../config";
import { EsgScoring } from "../content-items/EsgScorecard";
import { OrganizationAssessment } from "../content-items/OrganizationAssessment";
import { OrganizationAssessmentModal } from "../content-items/OrganizationAssessmentModal";
import { Questionnaires } from "../content-items/Questionnaires";
import { Rating } from "../content-items/Rating";

export interface ReviewsRiskSectionProps {
  organization?: OrgPublicProfileDto;
  organizationEntry?: OrganizationEntryDto;
}

const groupAssessmentsByTemplate = (
  assessments: AssessmentDto[] | undefined,
) => {
  if (!assessments) return {};

  return assessments.reduce<Record<string, AssessmentDto[]>>(
    (grouped, assessment) => {
      const templateId = assessment.template.templateId;
      if (!grouped[templateId]) {
        grouped[templateId] = [];
      }
      grouped[templateId].push(assessment);
      return grouped;
    },
    {},
  );
};

export const ReviewsRiskSection = ({
  organization,
  organizationEntry,
}: ReviewsRiskSectionProps) => {
  const orgData = organization || organizationEntry;

  if (!orgData) throw new Error("No organization data");

  const { t } = useTranslation();
  const showDummyData = requireBooleanEnvVar("VITE_ENABLE_DUMMY_COMPANY_PAGE");

  const {
    isOpen: isAssessmentModalOpen,
    onOpen: openAssessmentModal,
    onClose: closeAssessmentModal,
  } = useDisclosure();

  const { data: assessmentsData, isLoading } = useListAssessmentsQuery(
    orgData.id ? { orgId: orgData.id } : skipToken,
  );

  const groupedAssessments = useMemo(() => {
    return groupAssessmentsByTemplate(assessmentsData?.assessments);
  }, [assessmentsData]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );

  const { canCreateOrganizationAssessments: canCreateAssessment } = useSub();

  const handleAssessmentComplete = () => {
    closeAssessmentModal();
    setSelectedTemplateId(null);
  };

  const handleCreateAssessment = (templateId?: string) => {
    if (templateId) {
      setSelectedTemplateId(templateId);
    } else {
      setSelectedTemplateId(null);
    }
    openAssessmentModal();
  };

  return (
    <Box width="full">
      <VStack spacing={8} align="flex-start" width="full">
        <Flex
          pt="6"
          width="full"
          justifyContent="space-between"
          alignItems="center"
        >
          <Heading size="md">{t("Reviews & Risk")}</Heading>
          {canCreateAssessment && (
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              size="sm"
              variant="outline"
              onClick={() => handleCreateAssessment()}
            >
              {t("Create New Assessment")}
            </Button>
          )}
        </Flex>

        {isLoading ? (
          <Box
            width="full"
            py={10}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner size="xl" />
          </Box>
        ) : Object.keys(groupedAssessments).length === 0 ? (
          <Box
            width="full"
            py={10}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            borderWidth="1px"
            borderRadius="lg"
            p={5}
          >
            <Text fontSize="lg" mb={4}>
              {t("No assessments found")}
            </Text>
            {canCreateAssessment && (
              <Button
                leftIcon={<AddIcon />}
                colorScheme="blue"
                onClick={() => handleCreateAssessment()}
              >
                {t("Create your first assessment")}
              </Button>
            )}
          </Box>
        ) : (
          <SimpleGrid
            columns={{ base: 1, md: 2, xl: 3 }}
            spacing={4}
            width="full"
          >
            {Object.entries(groupedAssessments).map(
              ([templateId, assessments]) => (
                <OrganizationAssessment
                  key={templateId}
                  companyName={orgData.name}
                  assessments={assessments}
                  templateId={templateId}
                  onCreateAssessment={() => handleCreateAssessment(templateId)}
                />
              ),
            )}
          </SimpleGrid>
        )}
        <OrganizationAssessmentModal
          isOpen={isAssessmentModalOpen}
          onClose={closeAssessmentModal}
          organizationId={orgData.id}
          organizationName={orgData.name}
          onAssessmentComplete={handleAssessmentComplete}
          selectedTemplateId={selectedTemplateId}
        />
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8} width="full">
          {showDummyData && <EsgScoring />}
          {showDummyData && <Questionnaires />}
          {showDummyData && <Rating />}
        </SimpleGrid>
      </VStack>
    </Box>
  );
};
